import {
  DurationDisplay,
  FieldInformation,
  SelectField,
  Textarea,
  createListCollection
} from '@components'
import {
  type CreateSetup,
  type Duration,
  assetList,
  directionList,
  durationList,
  stageList,
} from '@here/api'
import { Field } from '@here/components'
import { type FormStore, getValue, setValue } from '@modular-forms/solid'
import { createEffect, createSignal } from 'solid-js'
import { Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import type { FormFieldType } from '.'
import { FieldGroupContainer } from './FieldGroupContainer'
import { ImageFormField } from './ImageFormField'
import { GeneralSchema } from './createSetupSchema'
import { fieldInformation } from './fieldInformation'

export default function General(props: {
	FormField: FormFieldType
	form: FormStore<CreateSetup, undefined>
}) {
	const [isValid, setIsValid] = createSignal(false)

	createEffect(() => {
		const symbol = getValue(props.form, 'symbol')
		const exchange = getValue(props.form, 'exchange')
		const description = getValue(props.form, 'description')
		const duration = getValue(props.form, 'duration')
		const market = getValue(props.form, 'market')
		const imageMacro = getValue(props.form, 'image_macro')
		const imagePattern = getValue(props.form, 'image_pattern')
		const imageTrend = getValue(props.form, 'image_trend')
		const imageTrigger = getValue(props.form, 'image_trigger')
		const createdBy = getValue(props.form, 'created_by')
		const imageTimeStop = getValue(props.form, 'image_time_stop')
		const imageVolumeByPrice = getValue(props.form, 'image_volume_by_price')

		const validateResult = v.safeParse(GeneralSchema, {
			symbol: symbol,
			exchange: exchange,
			description: description,
			duration: duration,
			market: market,
			image_macro: imageMacro,
			image_pattern: imagePattern,
			image_trend: imageTrend,
			image_trigger: imageTrigger,
			created_by: createdBy,
			image_time_stop: imageTimeStop,
			image_volume_by_price: imageVolumeByPrice,
		} as Partial<CreateSetup>)

		setIsValid(validateResult.success)
	})

	return (
		<FieldGroupContainer
			heading="General"
			valid={isValid()}
		>
			<Grid gap={4}>
				<Grid
					gridTemplateColumns={{
						base: '1fr 1fr',
						mdDown: '1fr',
					}}
				>
					<props.FormField name="title">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.title}>
									<Field.Label>Title</Field.Label>
								</FieldInformation>
								<Field.Input
									{...formFieldProps}
									value={field.value as any}
									type="text"
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>

					<props.FormField name="symbol">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error && props.form.submitted}>
								<FieldInformation information={fieldInformation.symbol}>
									<Field.Label>Symbol</Field.Label>
								</FieldInformation>
								<Field.Input
									type="text"
									{...formFieldProps}
									value={field.value as any}
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>

					<props.FormField name="exchange">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.exchange}>
									<Field.Label>Exchange</Field.Label>
								</FieldInformation>
								<Field.Input
									type="text"
									{...formFieldProps}
									value={field.value as any}
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>

					<props.FormField name="stage">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.stage}>
									<Field.Label>Stage</Field.Label>
								</FieldInformation>

								<SelectField
									formFieldProps={formFieldProps}
									field={field}
									collection={createListCollection({ items: stageList })}
									onChange={(e) => setValue(props.form, field.name, e.value[0] as any)}
								/>

								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>
				</Grid>

				<props.FormField name="description">
					{(field, formFieldProps) => (
						<Field.Root invalid={!!field.error && props.form.submitted}>
							<FieldInformation information={fieldInformation.description}>
								<Field.Label>Description</Field.Label>
							</FieldInformation>
							<Textarea
								{...formFieldProps}
								value={getValue(props.form, 'description')}
							/>
							<Field.ErrorText>
								{field.error && <div>{field.error}</div>}
							</Field.ErrorText>
						</Field.Root>
					)}
				</props.FormField>

				<Grid
					gridTemplateColumns={{
						base: '1fr 1fr 1fr',
						mdDown: '1fr',
					}}
				>
					<props.FormField name="position">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.position}>
									<Field.Label>Position</Field.Label>
								</FieldInformation>
								<SelectField
									formFieldProps={formFieldProps}
									field={field}
									collection={createListCollection({ items: directionList })}
									onChange={(e) => setValue(props.form, field.name, e.value[0] as any)}
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>

					<props.FormField name="duration">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.duration}>
									<Field.Label>Pattern Candlestick Time Frame</Field.Label>
								</FieldInformation>
								<SelectField
									formFieldProps={formFieldProps}
									field={field}
									innerTemplate={(value) => <DurationDisplay duration={value as Duration} />}
									collection={createListCollection({ items: durationList })}
									onChange={(e) => setValue(props.form, field.name, e.value[0] as any)}
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>

					<props.FormField name="market">
						{(field, formFieldProps) => (
							<Field.Root invalid={!!field.error}>
								<FieldInformation information={fieldInformation.market}>
									<Field.Label>Market</Field.Label>
								</FieldInformation>
								<SelectField
									formFieldProps={formFieldProps}
									field={field}
									collection={createListCollection({ items: assetList })}
									onChange={(e) => setValue(props.form, field.name, e.value[0] as any)}
								/>
								<Field.ErrorText>
									{field.error && <div>{field.error}</div>}
								</Field.ErrorText>
							</Field.Root>
						)}
					</props.FormField>
				</Grid>

				<ImageFormField
					name="image_macro"
					form={props.form}
					label="Macro View"
					FormField={props.FormField}
				/>

				<ImageFormField
					name="image_trend"
					form={props.form}
					label="Trend View"
					FormField={props.FormField}
				/>

				<ImageFormField
					name="image_pattern"
					form={props.form}
					label="Pattern View"
					FormField={props.FormField}
				/>

				<ImageFormField
					name="image_trigger"
					form={props.form}
					label="Trigger View"
					FormField={props.FormField}
				/>

				<ImageFormField
					name="image_volume_by_price"
					form={props.form}
					label="Volume By Price Chart"
					FormField={props.FormField}
				/>

				<ImageFormField
					name="image_time_stop"
					form={props.form}
					label="Time Stop Chart"
					FormField={props.FormField}
				/>

				<props.FormField name="created_by">
					{(field, props) => (
						<Field.Input
							display="none"
							type="text"
							{...props}
						/>
					)}
				</props.FormField>

				<props.FormField
					type="boolean"
					name="scan_tool_valid"
				>
					{(field, props) => (
						<Field.Input
							display="none"
							{...props}
						/>
					)}
				</props.FormField>

				<props.FormField name="id">
					{(field, props) => (
						<Field.Input
							display="none"
							type="text"
							{...props}
						/>
					)}
				</props.FormField>
			</Grid>
		</FieldGroupContainer>
	)
}
